import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { useCart } from "@/lib/CartContext";
import { toast } from "sonner";

export default function CartPage() {
  const { cart, updateItem, removeItem, checkout } = useCart();

  const onCheckout = async () => {
    try {
      const res = await checkout();
      if (res?.checkout_url) window.location.href = res.checkout_url;
    } catch { toast.error("Could not start checkout. Please try again."); }
  };

  return (
    <div className="max-w-site mx-auto px-6 md:px-10 py-14" data-testid="cart-page">
      <div className="label !text-[10px] mb-4">Your bag</div>
      <h1 className="font-display text-4xl md:text-5xl tracking-[-0.02em]">Bag {cart?.count ? <span className="text-[color:var(--ink-3)]">({cart.count})</span> : null}</h1>

      {(!cart || cart.lines.length === 0) ? (
        <div className="mt-14 border border-[color:var(--line)] p-10 text-center">
          <p className="editorial text-3xl mb-4">Empty for now.</p>
          <Link to="/preview/shop" className="label underline" data-testid="cart-page-shop">Start with a flavor →</Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 border border-[color:var(--line)]">
            {cart.lines.map((line) => (
              <div key={line.variant_id + line.subscribe} className="flex gap-6 p-6 border-b last:border-b-0 border-[color:var(--line)]" data-testid={`cart-page-line-${line.variant_id}`}>
                <img src={line.image} alt={line.flavor} className="w-28 h-32 object-contain bg-[color:var(--bg-soft)]" />
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-base">{line.product_title}</div>
                      <div className="label !text-[10px] mt-0.5">{line.flavor} · {line.variant_title}</div>
                      {line.subscribe && <div className="label !text-[10px] mt-1 text-[color:var(--accent-kelp)]">Subscribe & Save · 15% off</div>}
                    </div>
                    <button onClick={() => removeItem(line.variant_id)} className="label underline" data-testid={`cart-page-remove-${line.variant_id}`}>Remove</button>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center border border-[color:var(--line)]">
                      <button onClick={() => updateItem(line.variant_id, Math.max(0, line.quantity - 1))} className="w-9 h-9 flex items-center justify-center" data-testid={`cart-page-dec-${line.variant_id}`}><Minus size={12} /></button>
                      <span className="w-9 text-center text-sm">{line.quantity}</span>
                      <button onClick={() => updateItem(line.variant_id, line.quantity + 1)} className="w-9 h-9 flex items-center justify-center" data-testid={`cart-page-inc-${line.variant_id}`}><Plus size={12} /></button>
                    </div>
                    <div className="font-display text-lg">${line.line_total.toFixed(2)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <aside className="lg:col-span-4 border border-[color:var(--line)] p-6 h-max">
            <div className="label !text-[10px]">Order summary</div>
            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>${cart.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{cart.shipping === 0 ? "Free" : `$${cart.shipping.toFixed(2)}`}</span></div>
              <div className="h-px bg-[color:var(--line)] my-3" />
              <div className="flex justify-between text-base"><span>Total</span><span className="font-display">${cart.total.toFixed(2)}</span></div>
            </div>
            <button onClick={onCheckout} className="mt-6 w-full h-12 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.25em] hover:bg-[#2a2a2a] transition" data-testid="cart-page-checkout">
              Checkout
            </button>
            <p className="mt-4 label !text-[10px] text-[color:var(--ink-3)]">Secure checkout · 3rd party tested · cGMP USA</p>
          </aside>
        </div>
      )}
    </div>
  );
}
